import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { prettifyApiError } from "@/shared/api";
import { formatDateTime, formatMoney } from "@/shared/format";
import { AREA_CLS, Collapsible } from "@/shared/ui/Collapsible";
import { type CallReason, fetchOrderCalls, type OrderCall } from "../../api";
import { statoSostituzione } from "../../sostituzione";
import { useCustomerOrders } from "../../useCustomerOrders";
import type { Order } from "../../types";
import { CallLogDialog } from "../CallLogDialog";
import { useClipboardFeedback } from "@/shared/useClipboardFeedback";
import { CopyButton } from "../OrderCard";
import { IconCheck, IconCopy } from "../icons";
import { fatturazioneCoincide } from "./AddressesSection";
import { campiOltreLimite, ETICHETTE_CAMPO } from "../../limitiPoste";
import { IndirizzoDialog, valoriIndirizzo } from "./IndirizzoDialog";
import { BTN_SM, LABEL } from "./stile";

/** Email alias per-ordine dei marketplace (es. …@marketplace.amazon.it):
 *  stesso cliente su canali diversi = email diverse → LTV sottostima. */
function isMarketplaceAlias(email: string): boolean {
  return email.toLowerCase().includes("@marketplace.");
}

const CALL_REASON_LABELS: Record<CallReason, string> = {
  info_spedizione: "Info spedizione",
  reclamo: "Reclamo",
  reso_sostituzione: "Reso/Sostituzione",
  info_prodotto: "Info prodotto",
  sollecito_pagamento: "Sollecito pagamento",
  altro: "Altro",
};

/** "02/07" (it-IT) da un timestamp ms. */
function formatDayMonth(ms: number): string {
  return new Date(ms).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

/** Copia un blocco multi-riga con feedback effimero. ``CopyButton`` (riusato per
 *  le copie singole qui sotto) è icona-sola da 18px: questo è il gemello con
 *  etichetta, non un doppione del meccanismo (usa lo stesso hook). */
function CopyBlockButton({ text, label, title }: { text: string; label: string; title: string }) {
  const { copied, copy } = useClipboardFeedback();
  return (
    <button
      type="button"
      data-no-open
      title={copied ? "Copiato!" : title}
      aria-label={copied ? "Copiato negli appunti" : label}
      onClick={() => void copy(text)}
      className={[
        "inline-flex shrink-0 items-center gap-1 rounded-sm border px-1.5 py-0.5",
        "text-[10.5px] font-semibold leading-snug",
        copied
          ? "border-green bg-green-soft text-ink"
          : "border-line-2 bg-card text-ink hover:border-ink-4 hover:bg-paper-2 hover:text-ink",
      ].join(" ")}
    >
      {copied ? (
        <IconCheck className="h-[11px] w-[11px] shrink-0 fill-none stroke-current stroke-[3] [stroke-linecap:round] [stroke-linejoin:round]" />
      ) : (
        <IconCopy className="h-[11px] w-[11px] shrink-0 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
      )}
      {label}
    </button>
  );
}

/** Una stat dello storico (LTV / Ordini / Primo·ultimo). */
function Stat({ label, value, small }: { label: string; value: string; small?: boolean }) {
  return (
    <div className="min-w-[62px]">
      <div className={LABEL}>{label}</div>
      <div
        className={
          small
            ? "pt-[3px] text-[13px] font-medium text-ink"
            : "mt-px text-[17px] font-bold tabular-nums text-ink"
        }
      >
        {value}
      </div>
    </div>
  );
}

const PILL_CLASS =
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[12px] font-semibold leading-relaxed";

/** Chiamate cliente registrate per l'ordine: ultime 5. ⚠️ Presentazionale e
 *  SENZA il bottone «Registra chiamata»: quello vive nelle azioni del
 *  Collapsible, perché deve restare cliccabile a storico chiuso. L'errore di
 *  fetch nemmeno: sta nella fascia, sempre a video (vedi sotto). */
function CallsBlock({ calls }: { calls: OrderCall[] }) {
  return (
    <div className="mt-2 border-t border-line pt-2">
      <div className={`mb-1 ${LABEL}`}>Chiamate {calls.length > 0 && `(${calls.length})`}</div>
      {calls.length === 0 ? (
        <div className="text-[12.5px] text-ink">Nessuna chiamata registrata.</div>
      ) : (
        calls.slice(0, 5).map((call) => (
          <div key={call.id} className="border-t border-line py-1.5 text-[13px] first:border-t-0">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-ink">{CALL_REASON_LABELS[call.reason]}</span>
              <span className="shrink-0 text-[11.5px] text-ink">
                {formatDateTime(call.createdAt)}
              </span>
            </div>
            {call.note && (
              <div className="truncate text-[12px] text-ink" title={call.note}>
                {call.note}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

/** Il TERZO indirizzo dell'ordine, quando c'è: dove va la sostituzione.
 *
 *  Esiste solo se registrato — nessuna card vuota che chiede di essere
 *  ignorata su 4.000 ordini. Ambra finché l'etichetta non è partita, perché
 *  quello è un lavoro in sospeso che nessun altro punto della pagina ricorda. */
function ReplacementCard({ order }: { order: Order }) {
  const indirizzo = order.replacementAddress;
  if (!indirizzo) return null;
  const { giorniAperta, inRitardo, diverso } = statoSostituzione(order);
  // `replacementPending` = indirizzo registrato e NESSUNA sostituzione aperta
  // nei resi: è l'unico segnale che il browser ha già in mano per dire che
  // l'etichetta non è ancora nata. Non lo deduciamo da `label_path`.
  const senzaEtichetta = order.replacementPending === true;
  const destinatario =
    [indirizzo.name, indirizzo.surname].filter(Boolean).join(" ") || indirizzo.company;
  const luogo = [indirizzo.postcode, indirizzo.city, indirizzo.state_iso].filter(Boolean).join(" ");
  const quando =
    giorniAperta === null
      ? undefined
      : giorniAperta === 0
        ? "· inserito oggi"
        : `· inserito ${giorniAperta} ${giorniAperta === 1 ? "giorno" : "giorni"} fa`;

  return (
    <Collapsible
      id="sostituzione-indirizzo"
      title="Indirizzo di sostituzione"
      summary={quando}
      tone={senzaEtichetta ? "amber" : "neutral"}
      className="mb-2"
    >
      <div className="text-[13px] leading-normal">
        {destinatario && <div className="font-semibold text-ink">{destinatario}</div>}
        <div className="text-ink">{indirizzo.address}</div>
        {luogo && <div className="text-ink">{luogo}</div>}
        {indirizzo.phone && (
          <div className="font-mono text-[11.5px] text-ink">{indirizzo.phone}</div>
        )}
        {diverso && (
          <div className="mt-1.5 text-[11px] font-semibold text-amber">
            Diverso dall'indirizzo di spedizione
          </div>
        )}
        {order.replacementNote && (
          <div className="mt-1.5 border-t border-dotted border-line-2 pt-1.5 text-[11.5px] text-ink">
            {order.replacementNote}
          </div>
        )}
        {senzaEtichetta && (
          <div className="mt-1.5 text-[11px] text-ink">
            Etichetta non ancora emessa: mancano colli e misure dal pronto merce del fornitore.
          </div>
        )}
        {inRitardo && (
          <div className="mt-1 text-[11px] font-semibold text-red">
            Aperta da {giorniAperta} giorni: la cliente aspetta.
          </div>
        )}
      </div>
    </Collapsible>
  );
}

interface CustomerSectionProps {
  order: Order;
  /** Apre il dialog dei resi per registrare l'indirizzo di sostituzione.
   *  Assente = nessun bottone: un click che non fa niente è peggio del vuoto. */
  onAddReplacement?: () => void;
  /** Dopo la correzione a mano dell'indirizzo: ricarica l'ordine. Assente =
   *  niente «Modifica». */
  onAddressSaved?: () => void;
}

/** Fascia cliente: identità + contatti + indirizzo di SPEDIZIONE in chiaro.
 *  Il resto — LTV, altri ordini, chiamate — sta nel Collapsible «Storico
 *  cliente», che porta i numeri nel titolo: la colonna è larga 320px e ciò che
 *  non si guarda ad ogni ordine non può occuparla. */
export function CustomerSection({ order, onAddReplacement, onAddressSaved }: CustomerSectionProps) {
  const c = order.customer;
  const { data: c360 } = useCustomerOrders(order.id);
  const [calls, setCalls] = useState<OrderCall[]>([]);
  const [callsError, setCallsError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [indirizzoAperto, setIndirizzoAperto] = useState(false);
  // Avviso sulla scheda PRIMA della LDV: Poste rifiuta i campi lunghi (28/09).
  const oltrePoste = campiOltreLimite(valoriIndirizzo(order));

  const reloadCalls = useCallback(
    (signal?: AbortSignal) =>
      fetchOrderCalls(order.id, signal)
        .then((rows) => {
          if (signal?.aborted) return;
          setCalls(rows);
          setCallsError(null);
        })
        .catch((err: unknown) => {
          // L'abort in cleanup non è un errore da mostrare; tutto il resto sì:
          // lo stato vuoto NON è onesto se la fetch è fallita.
          if (signal?.aborted) return;
          setCallsError(prettifyApiError(err));
        }),
    [order.id],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    void reloadCalls(ctrl.signal);
    return () => ctrl.abort();
  }, [reloadCalls]);

  const otherOrders = (c360?.orders ?? []).filter((o) => o.id !== order.id);
  const initials = c.name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // mapOrder (api.ts:611) appiattisce GIÀ CAP + città + provincia dentro
  // ``city`` ("09095 Mogoro OR"): riconcatenare qui postcode/province li
  // duplicava su OGNI ordine — "Via X, 09095 09095 Mogoro OR (OR)" (bug #3).
  const addressLine = [c.address, c.city].filter(Boolean).join(", ");
  const addressText = addressLine + (c.country && c.country !== "IT" ? ` · ${c.country}` : "");
  const copyAllText = [c.name, c.address, c.city, c.country, c.phone, c.email]
    .map((v) => (v ?? "").trim())
    .filter(Boolean)
    .join("\n");

  // /customer espone first_order_at/last_order_at ma fetchCustomerOrders non li
  // mappa: derivarli da orders[] è equivalente — il backend li calcola dalle
  // STESSE righe che diventano ``orders`` (router.py:606-620).
  const orderTimes = (c360?.orders ?? [])
    .map((o) => (o.createdAtPoleepo ? Date.parse(o.createdAtPoleepo) : Number.NaN))
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b);
  const firstOrderAt = orderTimes[0];
  const lastOrderAt = orderTimes[orderTimes.length - 1];
  const firstLast =
    firstOrderAt !== undefined && lastOrderAt !== undefined
      ? `${formatDayMonth(firstOrderAt)} · ${formatDayMonth(lastOrderAt)}`
      : "n/d";

  // La scheda cliente esiste solo per chi ha un'email: la chiave è ``e:<email>``
  // (repository.py:572) e /clienti non elenca i match deboli nome+città.
  // Senza email il link non si renderizza (D1: omesso, non spento).
  const customerKey = c.email.trim() ? `e:${c.email.trim()}` : null;

  // Il confronto con la fatturazione vive in AddressesSection (una sola
  // risposta alla stessa domanda): qui è la pill che evita di aprirla.
  const coincideFattura = fatturazioneCoincide(order);

  // Il numero che si legge da CHIUSO. Aprire per scoprire «nessuna chiamata»
  // è esattamente il click che questa pagina esiste per risparmiare.
  const chiamateSummary = callsError
    ? "chiamate non caricate"
    : calls.length === 0
      ? "nessuna chiamata"
      : `${calls.length} ${calls.length === 1 ? "chiamata" : "chiamate"}`;
  const storicoSummary = [
    c360 ? `LTV ${formatMoney(c360.ltv, c360.currency)}` : null,
    c360 ? `${c360.ordersCount} ${c360.ordersCount === 1 ? "ordine" : "ordini"}` : null,
    chiamateSummary,
  ]
    .filter(Boolean)
    .map((p) => `· ${p}`)
    .join(" ");

  return (
    <>
      <section
        className={`mb-3 overflow-hidden rounded-xl border border-line bg-card shadow-[0_1px_2px_rgba(31,27,22,0.04)] ${AREA_CLS.cliente}`}
      >
        <div className="flex flex-wrap items-start gap-x-6 gap-y-3 px-4 py-4">
          <div className="flex min-w-0 flex-[1_1_100%] items-start gap-3">
            <span
              aria-hidden="true"
              className="grid h-10 w-10 flex-none place-items-center rounded-full bg-violet-soft text-[14px] font-bold tracking-[0.02em] text-violet-2"
            >
              {initials || "?"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[17px] font-bold leading-tight text-ink">{c.name}</span>
                {copyAllText && (
                  <CopyBlockButton
                    text={copyAllText}
                    label="Copia tutto"
                    title="Copia nome, indirizzo, telefono e mail in un blocco solo"
                  />
                )}
              </div>
              <div className="mt-1.5 flex flex-col items-start gap-1 text-[13px] text-ink">
                {c.phone && (
                  <span className="flex max-w-full items-center gap-1">
                    {/* tel: non tollera spazi: "+39 701 7679" → "+397017679". */}
                    <a
                      href={`tel:${c.phone.replace(/\s+/g, "")}`}
                      className="truncate font-mono text-[12.5px] text-blue hover:underline"
                    >
                      {c.phone}
                    </a>
                    <CopyButton text={c.phone} label="Copia telefono" />
                  </span>
                )}
                {c.email && (
                  <span className="flex max-w-full items-center gap-1">
                    <a
                      href={`mailto:${c.email}`}
                      className="truncate text-blue hover:underline"
                      title={c.email}
                    >
                      {c.email}
                    </a>
                    <CopyButton text={c.email} label="Copia email" />
                  </span>
                )}
              </div>
              {(addressText || onAddressSaved) && (
                <div
                  className={`mt-2.5 text-[13px] leading-normal text-ink ${
                    oltrePoste.length > 0
                      ? "rounded-lg border border-amber bg-amber-soft px-2.5 py-2"
                      : "border-t border-line pt-2.5"
                  }`}
                >
                  <span className={`mb-0.5 flex flex-wrap items-center gap-1.5 ${LABEL}`}>
                    Indirizzo di spedizione
                    {c.addressEditedAt && (
                      <span
                        className="font-semibold text-ink"
                        title={`Corretto a mano il ${formatDateTime(c.addressEditedAt)}`}
                      >
                        · corretto in OM
                      </span>
                    )}
                    {onAddressSaved && (
                      <button
                        type="button"
                        aria-label="Modifica indirizzo"
                        onClick={() => setIndirizzoAperto(true)}
                        className="rounded-md px-1 text-[12px] font-semibold text-ink underline decoration-line-2 decoration-2 underline-offset-4 hover:decoration-ink-4"
                      >
                        Modifica
                      </button>
                    )}
                  </span>
                  <span className="inline-flex flex-wrap items-center gap-1">
                    {/* Frase intera in un solo nodo di testo: RTL getNodeText non
                        attraversa i figli e i test asseriscono l'indirizzo pieno. */}
                    <span>{addressText}</span>
                    <CopyButton text={addressText} label="Copia l'indirizzo" />
                  </span>
                  {oltrePoste.length > 0 && (
                    <p className="mt-1 text-[12.5px] text-ink">
                      <span className="font-bold">Troppo lungo per Poste Delivery:</span>{" "}
                      {oltrePoste
                        .map(
                          (o) =>
                            `${ETICHETTE_CAMPO[o.campo].toLowerCase()} ${o.lunghezza}/${o.max}`,
                        )
                        .join(" · ")}
                      {onAddressSaved && (
                        <button
                          type="button"
                          onClick={() => setIndirizzoAperto(true)}
                          className="ml-2 rounded-md border border-ink bg-card px-2 py-0.5 text-[11px] font-semibold text-ink hover:bg-amber-soft"
                        >
                          Correggi
                        </button>
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {c360?.ordersCount === 1 && (
              <span className={`${PILL_CLASS} border-line-2 text-ink`}>primo ordine</span>
            )}
            {coincideFattura !== null && (
              <span
                className={
                  coincideFattura
                    ? `${PILL_CLASS} border-line-2 text-ink`
                    : `${PILL_CLASS} border-amber bg-amber-soft font-semibold text-ink`
                }
              >
                {coincideFattura ? "= fatturazione" : "≠ fatturazione"}
              </span>
            )}
            {customerKey && (
              <Link
                to={`/clienti/${encodeURIComponent(customerKey)}`}
                className={`${PILL_CLASS} border-violet-soft bg-violet-soft font-semibold text-violet-2 hover:border-violet`}
              >
                Scheda cliente
              </Link>
            )}
            {/* Discreta apposta: la sostituzione è l'eccezione, non il flusso. */}
            {onAddReplacement && !order.replacementAddress && (
              <button
                type="button"
                onClick={onAddReplacement}
                className="rounded-md px-1 py-0.5 text-[12px] font-semibold text-ink underline decoration-line-2 decoration-2 underline-offset-4 hover:text-ink hover:decoration-ink-4"
              >
                + Indirizzo di sostituzione
              </button>
            )}
          </div>
        </div>

        {/* Fetch chiamate fallita: sta FUORI dal Collapsible, sempre a video.
            Un 500 mostrato come «nessuna chiamata» è un'affermazione falsa sui
            dati — e l'operatore richiama un cliente già contattato. Dentro una
            piega chiusa sarebbe altrettanto invisibile. */}
        {callsError && (
          <div role="alert" className="border-t border-line px-4 py-2 text-[12.5px] text-red">
            Chiamate non caricate: {callsError}
          </div>
        )}

        {c360 && (isMarketplaceAlias(c360.customer.email) || c360.customer.isFallbackKey) && (
          <div className="border-t border-line px-4 py-2 text-[12px] text-ink">
            {c360.customer.isFallbackKey && (
              <div>match debole: cliente riconosciuto per nome+città (email assente)</div>
            )}
            {isMarketplaceAlias(c360.customer.email) && (
              <div>
                email alias del marketplace: ordini dello stesso cliente su altri canali non
                collegabili — il totale può sottostimare
              </div>
            )}
          </div>
        )}
      </section>

      {indirizzoAperto && onAddressSaved && (
        <IndirizzoDialog
          order={order}
          onClose={() => setIndirizzoAperto(false)}
          onSaved={onAddressSaved}
        />
      )}

      <ReplacementCard order={order} />

      <Collapsible
        id="storico-cliente"
        area="cliente"
        title="Storico cliente"
        summary={storicoSummary}
        className="mb-4"
        actions={
          // Etichetta corta: a 320px «Registra chiamata» mangiava il riassunto
          // del titolo, che è il motivo per cui la sezione si può tenere chiusa.
          <button
            type="button"
            aria-label="Registra chiamata"
            title="Registra chiamata"
            onClick={() => setShowDialog(true)}
            className={BTN_SM}
          >
            + Chiamata
          </button>
        }
      >
        {c360 && (
          <div className="flex flex-wrap gap-5">
            <Stat label="LTV (ricavo lordo)" value={formatMoney(c360.ltv, c360.currency)} />
            <Stat label="Ordini" value={String(c360.ordersCount)} />
            <Stat label="Primo · ultimo" small value={firstLast} />
          </div>
        )}

        {otherOrders.length > 0 && (
          <div className="mt-2 border-t border-line pt-2">
            <div className={`mb-1 ${LABEL}`}>Altri ordini ({otherOrders.length})</div>
            {otherOrders.map((o) => (
              <Link
                key={o.id}
                to={`/ordini/${encodeURIComponent(o.id)}`}
                className="flex items-center justify-between gap-2 rounded-md border-t border-line px-1 py-1.5 text-[13px] hover:bg-paper"
              >
                <span className="min-w-0">
                  <span className="block truncate font-mono text-[12.5px] text-ink">{o.id}</span>
                  <span className="block text-[11.5px] text-ink">
                    {[formatDateTime(o.createdAtPoleepo ?? null), o.state]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatMoney(o.total, o.currency)}
                </span>
              </Link>
            ))}
          </div>
        )}

        {!callsError && <CallsBlock calls={calls} />}
      </Collapsible>

      {showDialog && (
        <CallLogDialog
          order={{
            reference: order.id,
            customerName: c.name,
            customerEmail: c.email,
            customerPhone: c.phone,
          }}
          onClose={() => setShowDialog(false)}
          onSaved={() => void reloadCalls()}
        />
      )}
    </>
  );
}
